import { getApiConfig } from "@/lib/api/config";
import {
  clearAppTokens,
  clearClientAuthState,
  getAccessToken,
  getRefreshToken,
  setAppTokens,
} from "@/lib/api/auth-token";
import { ApiError } from "@/lib/api/errors";
import { getAuthMode } from "@/lib/auth/auth-mode";
import {
  ensureValidAccessToken,
  getValidAccessToken,
} from "@/lib/auth/token-session";

export { isSamlAuthEnabled, getAuthMode } from "@/lib/auth/auth-mode";
export {
  getSamlLoginUrl,
  getSamlMetadataUrl,
  getSamlCallbackPath,
  getSamlCallbackUrl,
  isSamlCallbackPath,
  hasSamlHandoffParams,
} from "@/lib/auth/saml-config";
export {
  resolveUserRole,
  resolveUserRoles,
  resolveUserPermissions,
  collectApiPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessRoute,
  getDefaultHomePath,
  navigateToRoleHome,
  getNavItemsForPermissions,
  APP_ROLES,
  PERMISSIONS,
  ROUTE_RULES,
  LANDING_PATH_PRIORITY,
  ROLE_ALIASES,
  type AppRole,
  type Permission,
  type NavItem,
} from "@/lib/auth/rbac";
export { buildMockAuthUser } from "@/lib/auth/mock-user";
export {
  loginWithSaml,
  completeSamlCallback,
  ensureSamlSession,
  navigateAfterSamlLogin,
  finishSamlSignInAndNavigate,
  logoutSaml,
} from "@/lib/auth/saml-auth";
export { fetchAndStoreCurrentUser } from "@/lib/auth/user-session";
export {
  storeTokensFromHandoff,
  refreshAppTokens,
  getValidAccessToken,
  ensureValidAccessToken,
} from "@/lib/auth/token-session";
export {
  getLoginPath,
  readLoginErrorFromLocation,
  clearLoginErrorFromLocation,
  formatLoginErrorMessage,
  redirectToLoginWithError,
} from "@/lib/auth/login-error";

/** @deprecated Use navigateAfterSamlLogin */
export { navigateAfterSamlLogin as navigateAfterMicrosoftLogin } from "@/lib/auth/saml-auth";
/** @deprecated Use navigateAfterSamlLogin */
export { navigateAfterSamlLogin as navigateToDashboard } from "@/lib/auth/saml-auth";
/** @deprecated Use ensureSamlSession */
export { ensureSamlSession as ensureMicrosoftSession } from "@/lib/auth/saml-auth";

export async function initializeAuth(): Promise<void> {
  if (getAuthMode() === "mock") return;
  await ensureValidAccessToken();
}

import {
  clearAuthProfile,
  getAuthProfile,
  storeAuthProfile,
} from "@/lib/auth/profile";
import type { AuthProfile } from "@/types/auth-types";
import { loginWithSaml, logoutSaml } from "@/lib/auth/saml-auth";

export async function login(): Promise<void> {
  const mode = getAuthMode();

  switch (mode) {
    case "saml":
      loginWithSaml();
      return;
    case "backend": {
      const { authLoginUrl } = getApiConfig();
      if (authLoginUrl) {
        window.location.href = authLoginUrl;
        return;
      }
      const session = await createBackendSession();
      setAppTokens(session);
      return;
    }
    case "mock":
      setAppTokens({ accessToken: "mock_access_token" });
      storeAuthProfile({
        name: "Demo User",
        email: "demo@company.com",
      });
      return;
  }
}

export async function logout(): Promise<void> {
  const mode = getAuthMode();

  if (mode === "saml") {
    await logoutSaml();
    return;
  }

  try {
    if (mode === "backend" && getApiConfig().baseUrl) {
      await backendLogout();
    }
  } catch {
    // ignore backend logout errors during sign-out
  } finally {
    clearAppTokens();
    clearAuthProfile();
    if (typeof window !== "undefined") {
      window.location.replace(`${window.location.origin}/`);
    }
  }
}

/** Clear all client auth storage and reload to the login page. */
export async function signOutAndReload(): Promise<void> {
  const mode = getAuthMode();

  try {
    if (mode === "saml") {
      await logoutSaml();
      return;
    }
    if (mode === "backend" && getApiConfig().baseUrl) {
      await backendLogout();
    }
  } catch {
    // ignore backend logout errors during sign-out
  }

  clearClientAuthState();

  if (typeof window !== "undefined") {
    window.location.replace(`${window.location.origin}/`);
  }
}

export async function resolveAccessToken(): Promise<string | null> {
  if (getAuthMode() === "mock") {
    return getAccessToken();
  }
  return getValidAccessToken();
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken()?.trim() || getRefreshToken()?.trim());
}

export function getProfile(): AuthProfile {
  return (
    getAuthProfile() ?? {
      name: "User",
      email: "user@company.com",
    }
  );
}

async function createBackendSession(): Promise<{
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}> {
  const { baseUrl } = getApiConfig();
  const response = await fetch(`${baseUrl}/v1/auth/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new ApiError(
      "We couldn't start your session. Please sign in again.",
      response.status,
    );
  }

  return (await response.json()) as {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
  };
}

async function backendLogout(): Promise<void> {
  const { baseUrl } = getApiConfig();
  const token = getAccessToken();
  await fetch(`${baseUrl}/v1/auth/logout`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}
